<template>
  <ModalBasicTailwind
    id="predix-favorite-alloy-dialog"
    size="lg"
    :modal-open="modalOpen"
    title="Saved alloys"
    subtitle="After you save an alloy, you can compare its predicted values with standard alloys and your other saved alloys."
    @close-modal="close"
  >
    <div class="predix-fav-tabs predix-result-tabs" role="tablist">
      <button
        type="button"
        role="tab"
        class="predix-result-tab predix-fav-tabs__tab"
        :class="activeTab === 'save' ? 'predix-result-tab--active predix-fav-tabs__tab--active' : 'predix-result-tab--idle'"
        :aria-selected="activeTab === 'save'"
        @click="activeTab = 'save'"
      >
        Save current
      </button>
      <button
        type="button"
        role="tab"
        class="predix-result-tab predix-fav-tabs__tab"
        :class="activeTab === 'list' ? 'predix-result-tab--active predix-fav-tabs__tab--active' : 'predix-result-tab--idle'"
        :aria-selected="activeTab === 'list'"
        @click="activeTab = 'list'"
      >
        My saved alloys
        <span
          v-if="savedAlloys.length"
          class="predix-fav-tabs__count"
          :class="{ 'predix-fav-tabs__count--active': activeTab === 'list' }"
        >{{ savedAlloys.length }}</span>
      </button>
    </div>

    <div class="px-5 py-4 space-y-4">
      <template v-if="activeTab === 'save'">
        <p v-if="!canSaveCurrent" class="predix-fav-note">
          Run a Main DB prediction with results before you can save the current alloy.
        </p>

        <div :class="{ 'predix-fav-disabled': !canSaveCurrent }">
          <div>
            <div class="predix-fav-label">
              Alloy name
              <span class="predix-fav-required" aria-hidden="true">*</span>
            </div>
            <div class="predix-fav-name">
              <span class="predix-fav-name__prefix" aria-hidden="true">S</span>
              <input
                id="predix-fav-digits"
                class="predix-fav-name__input"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                maxlength="7"
                autocomplete="off"
                placeholder="1"
                :value="displayDigits"
                :disabled="!canSaveCurrent"
                @input="onDigitsInput($event.target.value)"
              />
            </div>
          </div>

          <div class="predix-fav-summary">
            <div class="predix-fav-summary__row">
              <span>Composition elements</span>
              <strong>{{ compositionCount }}</strong>
            </div>
            <div class="predix-fav-summary__row">
              <span>Predicted properties</span>
              <strong>{{ propertyCount }}</strong>
            </div>
          </div>
        </div>

        <p v-if="submitError" class="text-sm text-red-600 dark:text-red-400">
          {{ submitError }}
        </p>
      </template>

      <template v-else>
        <p v-if="listError" class="text-sm text-red-600 dark:text-red-400">
          {{ listError }}
        </p>

        <div v-if="!savedAlloys.length" class="predix-fav-empty">
          <v-icon size="36" color="grey lighten-1">mdi-bookmark-outline</v-icon>
          <p>No saved alloys yet.</p>
          <p class="predix-fav-empty__hint">Save a Main DB prediction from the Save current tab to use it in comparison.</p>
        </div>

        <ul v-else class="predix-fav-list">
          <li v-for="alloy in sortedSavedAlloys" :key="alloy.id" class="predix-fav-list__item">
            <div class="predix-fav-list__main">
              <span class="predix-fav-list__name">{{ formatSavedAlloyDisplay(alloy.name) }}</span>
              <span class="predix-fav-list__meta">
                {{ alloyMeta(alloy) }}
              </span>
            </div>
            <button
              type="button"
              class="predix-fav-list__remove"
              :disabled="deletingId === alloy.id"
              @click="confirmRemove(alloy)"
            >
              {{ deletingId === alloy.id ? 'Removing…' : 'Remove' }}
            </button>
          </li>
        </ul>
      </template>
    </div>

    <div class="px-5 py-4 border-t border-gray-200 dark:border-gray-700/60">
      <div class="flex flex-wrap justify-end gap-2">
        <CTailwindBtn type="button" variant="outline" @click="close">
          Close
        </CTailwindBtn>
        <CTailwindBtn
          v-if="activeTab === 'save'"
          type="button"
          variant="primary"
          :disabled="!canSave || saving || !canSaveCurrent"
          @click="save"
        >
          {{ saving ? 'Saving…' : 'Save alloy' }}
        </CTailwindBtn>
      </div>
    </div>
  </ModalBasicTailwind>
</template>

<script>
import ModalBasicTailwind from '@/components/modal/ModalBasicTailwind.vue'
import CTailwindBtn from '@/components/Vuetify/CTailwindBtn.vue'
import { createFavoriteAlloy, deleteFavoriteAlloy } from '@/api/favoriteAlloys'

const NAME_RE = /^S[1-9]\d{0,6}$/

export default {
  name: 'PredixFavoriteAlloyDialog',
  components: {
    ModalBasicTailwind,
    CTailwindBtn,
  },
  props: {
    modalOpen: {
      type: Boolean,
      default: false,
    },
    initialTab: {
      type: String,
      default: 'save',
    },
    canSaveCurrent: {
      type: Boolean,
      default: true,
    },
    existingNames: {
      type: Array,
      default: () => [],
    },
    savedAlloys: {
      type: Array,
      default: () => [],
    },
    compositionItems: {
      type: Array,
      default: () => [],
    },
    propertyItems: {
      type: Array,
      default: () => [],
    },
    packageId: {
      type: Number,
      default: -1,
    },
    databaseId: {
      type: Number,
      default: -1,
    },
    databaseName: {
      type: String,
      default: '',
    },
    heatTreatmentGroupId: {
      type: [Number, String],
      default: null,
    },
    castingGroupId: {
      type: [Number, String],
      default: null,
    },
  },
  data: () => ({
    activeTab: 'save',
    digits: '',
    saving: false,
    submitError: '',
    listError: '',
    deletingId: null,
  }),
  computed: {
    fullName() {
      return this.digits ? `S${this.digits}` : ''
    },
    displayDigits() {
      if (!this.digits) return ''
      // UI formatting only: show fixed 4-digit numeric part.
      return String(this.digits).padStart(6, '0')
    },
    compositionCount() {
      return (this.compositionItems || []).length
    },
    propertyCount() {
      return (this.propertyItems || []).length
    },
    canSave() {
      return NAME_RE.test(this.fullName) && this.propertyCount > 0
    },
    sortedSavedAlloys() {
      return [...(this.savedAlloys || [])].sort((a, b) => {
        const an = String(a && a.name || '')
        const bn = String(b && b.name || '')
        return an.localeCompare(bn, undefined, { numeric: true })
      })
    },
  },
  watch: {
    modalOpen(open) {
      if (open) {
        this.activeTab = this.defaultOpenTab()
        this.submitError = ''
        this.listError = ''
        this.saving = false
        this.deletingId = null
        this.digits = this.nextUnusedDigits()
      }
    },
  },
  methods: {
    defaultOpenTab() {
      if (this.initialTab === 'list') return 'list'
      return this.canSaveCurrent ? 'save' : 'list'
    },
    nextUnusedDigits() {
      const used = new Set()
      ;(this.existingNames || []).forEach((name) => {
        const match = String(name || '').match(/^[FS]([1-9]\d{0,6})$/i)
        if (match) used.add(Number(match[1]))
      })
      let n = 1
      while (used.has(n) && n < 9999999) n += 1
      return String(n)
    },
    onDigitsInput(raw) {
      const digits = String(raw || '').replace(/\D/g, '').replace(/^0+/, '').slice(0, 7)
      this.digits = digits
      this.submitError = ''
    },
    alloyMeta(alloy) {
      const items = (alloy && alloy.items) || []
      const props = items.filter((item) => item && item.kind === 'property').length
      const parts = []
      if (props) parts.push(`${props} propert${props === 1 ? 'y' : 'ies'}`)
      const created = alloy && (alloy.createdAt || alloy.created_at)
      if (created) parts.push(this.formatDate(created))
      return parts.join(' · ') || 'Saved alloy'
    },
    formatDate(value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },
    close() {
      if (this.saving || this.deletingId) return
      this.$emit('close-modal')
    },
    itemValue(item) {
      if (!item || item.value === undefined || item.value === null) return ''
      return String(item.value)
    },
    save() {
      if (!this.canSave || this.saving || !this.canSaveCurrent) return
      const name = this.fullName
      const existing = (this.existingNames || []).some(
        (n) => String(n).toUpperCase() === name.toUpperCase()
      )
      if (existing) {
        this.submitError = 'You already have a saved alloy with this name.'
        return
      }
      const items = []
      ;(this.compositionItems || []).forEach((item) => {
        if (!item || !item.name) return
        items.push({
          kind: 'composition',
          name: item.name,
          value: this.itemValue(item),
        })
      })
      ;(this.propertyItems || []).forEach((item) => {
        if (!item || !item.name) return
        items.push({
          kind: 'property',
          name: item.name,
          value: this.itemValue(item),
        })
      })
      const payload = {
        name,
        packageId: this.packageId,
        databaseId: this.databaseId,
        databaseName: this.databaseName || '',
        heatTreatmentGroupId: this.heatTreatmentGroupId == null ? null : Number(this.heatTreatmentGroupId),
        castingGroupId: this.castingGroupId == null ? null : Number(this.castingGroupId),
        items,
      }
      this.saving = true
      this.submitError = ''
      createFavoriteAlloy(
        payload,
        (res) => {
          this.saving = false
          this.$emit('saved', res)
          this.activeTab = 'list'
          this.digits = this.nextUnusedDigits()
        },
        (err) => {
          this.saving = false
          this.submitError = this.parseError(err)
        }
      )
    },
    confirmRemove(alloy) {
      if (!alloy || alloy.id == null || this.deletingId) return
      const label = alloy.name || 'this alloy'
      if (!window.confirm(`Remove ${label}? It will no longer appear in Alloy Comparison.`)) return
      this.removeAlloy(alloy)
    },
    removeAlloy(alloy) {
      this.deletingId = alloy.id
      this.listError = ''
      deleteFavoriteAlloy(
        alloy.id,
        () => {
          this.deletingId = null
          this.$emit('deleted', alloy)
        },
        () => {
          this.deletingId = null
          this.listError = 'Could not remove this saved alloy. Try again.'
        }
      )
    },
    parseError(err) {
      const data = err && err.data
      if (!data) return 'Could not save this alloy. Try again.'
      if (typeof data.name === 'string') return data.name
      if (Array.isArray(data.name) && data.name[0]) return String(data.name[0])
      if (typeof data.detail === 'string') return data.detail
      if (err.status === 409) return 'You already have a saved alloy with this name.'
      return 'Could not save this alloy. Try again.'
    },
    formatSavedAlloyDisplay(name) {
      const raw = String(name || '')
      const match = raw.match(/^[FS]([1-9]\d{0,6})$/i)
      if (!match) return raw
      const digits = String(match[1])
      return `S ${digits.padStart(6, '0')}`
    },
  },
}
</script>

<style scoped>
.predix-fav-tabs {
  display: flex;
  gap: 0.25rem;
  padding: 0 1.25rem;
  border-bottom: 1px solid var(--color-gray-200);
}

.predix-fav-tabs.predix-result-tabs {
  background: transparent;
}

.predix-fav-tabs__tab {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  margin-bottom: -1px;
  cursor: pointer;
}

.predix-fav-tabs__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.3125rem;
  margin-left: 0.375rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: var(--font-weight-semibold, 600);
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: var(--v-primary-base);
  background-color: color-mix(in srgb, var(--v-primary-base) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--v-primary-base) 22%, transparent);
}

.predix-fav-tabs__count--active {
  color: #fff;
  background-color: var(--v-primary-base);
  border-color: color-mix(in srgb, var(--v-primary-base) 82%, black);
}

.predix-fav-note {
  margin: 0;
  font-size: 0.875rem;
  color: var(--color-gray-600);
}

.predix-fav-disabled {
  opacity: 0.55;
  pointer-events: none;
}

.predix-fav-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-gray-800);
  margin-bottom: 0.5rem;
}

.predix-fav-required {
  color: #dc2626;
  margin-left: 0.125rem;
}

.predix-fav-name {
  display: flex;
  align-items: stretch;
  border: 1px solid var(--color-gray-200);
  border-radius: 0.5rem;
  overflow: hidden;
  background: #fff;
}

.predix-fav-name__prefix {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.5rem;
  padding: 0 0.75rem;
  font-weight: 700;
  font-size: 1rem;
  color: var(--v-primary-base);
  background: color-mix(in srgb, var(--v-primary-base) 10%, #fff);
  border-right: 1px solid var(--color-gray-200);
}

.predix-fav-name__input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: none;
  padding: 0.625rem 0.75rem;
  font-size: 0.9375rem;
  font-variant-numeric: tabular-nums;
  background: transparent;
  color: var(--color-gray-900);
}

.predix-fav-summary {
  margin-top: 1rem;
  border: 1px solid var(--color-gray-200);
  border-radius: 0.5rem;
  padding: 0.75rem 0.875rem;
  background: color-mix(in srgb, var(--color-gray-100) 50%, #fff);
}

.predix-fav-summary__row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: var(--color-gray-700);
}

.predix-fav-summary__row + .predix-fav-summary__row {
  margin-top: 0.375rem;
}

.predix-fav-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.375rem;
  padding: 1.5rem 0.5rem;
  color: var(--color-gray-600);
}

.predix-fav-empty__hint {
  margin: 0;
  max-width: 22rem;
  font-size: 0.8125rem;
  color: var(--color-gray-500);
}

.predix-fav-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.predix-fav-list__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  border: 1px solid var(--color-gray-200);
  border-radius: 0.5rem;
  background: color-mix(in srgb, var(--color-gray-100) 35%, #fff);
}

.predix-fav-list__main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.predix-fav-list__name {
  font-weight: 700;
  font-size: 0.9375rem;
  color: var(--color-gray-900);
}

.predix-fav-list__meta {
  font-size: 0.8125rem;
  color: var(--color-gray-500);
}

.predix-fav-list__remove {
  flex: 0 0 auto;
  border: 1px solid color-mix(in srgb, #dc2626 35%, var(--color-gray-200));
  background: #fff;
  color: #dc2626;
  border-radius: 0.375rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}

.predix-fav-list__remove:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

html.dark .predix-fav-tabs,
.v-application.theme--dark .predix-fav-tabs {
  border-bottom-color: color-mix(in srgb, var(--color-gray-600) 70%, transparent);
}

html.dark .predix-fav-tabs__count,
.v-application.theme--dark .predix-fav-tabs__count {
  color: color-mix(in srgb, var(--v-primary-base) 70%, white);
  background-color: color-mix(in srgb, var(--v-primary-base) 18%, transparent);
  border-color: color-mix(in srgb, var(--v-primary-base) 32%, transparent);
}

html.dark .predix-fav-tabs__count--active,
.v-application.theme--dark .predix-fav-tabs__count--active {
  color: #fff;
  background-color: var(--v-primary-base);
  border-color: color-mix(in srgb, var(--v-primary-base) 78%, black);
}

html.dark .predix-fav-label,
.v-application.theme--dark .predix-fav-label {
  color: var(--color-gray-100);
}

html.dark .predix-fav-name,
.v-application.theme--dark .predix-fav-name {
  background: color-mix(in srgb, var(--color-gray-900) 45%, var(--color-gray-800));
  border-color: color-mix(in srgb, var(--color-gray-600) 70%, transparent);
}

html.dark .predix-fav-name__prefix,
.v-application.theme--dark .predix-fav-name__prefix {
  background: color-mix(in srgb, var(--v-primary-base) 18%, var(--color-gray-800));
  border-right-color: color-mix(in srgb, var(--color-gray-600) 70%, transparent);
  color: #fff;
}

html.dark .predix-fav-name__input,
.v-application.theme--dark .predix-fav-name__input {
  color: #fff;
}

html.dark .predix-fav-summary,
.v-application.theme--dark .predix-fav-summary {
  background: color-mix(in srgb, var(--color-gray-900) 40%, var(--color-gray-800));
  border-color: color-mix(in srgb, var(--color-gray-600) 70%, transparent);
}

html.dark .predix-fav-summary__row,
.v-application.theme--dark .predix-fav-summary__row {
  color: var(--color-gray-200);
}

html.dark .predix-fav-list__item,
.v-application.theme--dark .predix-fav-list__item {
  background: color-mix(in srgb, var(--color-gray-900) 40%, var(--color-gray-800));
  border-color: color-mix(in srgb, var(--color-gray-600) 70%, transparent);
}

html.dark .predix-fav-list__name,
.v-application.theme--dark .predix-fav-list__name {
  color: #fff;
}

html.dark .predix-fav-list__remove,
.v-application.theme--dark .predix-fav-list__remove {
  background: color-mix(in srgb, var(--color-gray-900) 50%, var(--color-gray-800));
  border-color: color-mix(in srgb, #f87171 45%, transparent);
  color: #fca5a5;
}
</style>
